'use client'

import { useStore } from 'jotai'
import { Popover as PopoverPrimitive } from 'radix-ui'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { useWatchlistLiveTicker } from '@/features/dashboard/hooks/use-watchlist-live-ticker'
import { resolvePriceChangePercent24h } from '@/features/dashboard/lib/ticker-change'
import { watchlistEntryKey } from '@/features/dashboard/lib/watchlist-entry'
import { getExchangeIconKey, getExchangeLabel } from '@/lib/configs/exchange-registry'
import { toExchangeString } from '@/lib/proto/util'
import { cn } from '@/lib/utils'
import { displayPriceOf } from '@/lib/utils/display-price'
import { formatFirstLetterToUpperCase, formatSymbol } from '@/lib/utils/format-util'
import { useMultiIncrementalInstrumentStream } from '@/service/ws/use-incremental-instrument'
import { useMultiMarketDataStream } from '@/service/ws/use-market-data-stream'
import { scheduleTickerMapWrite, watchListTickerMapAtom } from '@/state/dashboard'
import {
  Exchange,
  type IncrementalTickerList,
  MarketDataType,
  type TickerList,
} from '@/types/generated/common/types'

// Matches the old per-tab HoverCard's openDelay/closeDelay — see useTabHoverCard for why
// these are now driven manually instead of by Radix's own per-trigger delay logic.
const TAB_HOVER_CARD_OPEN_DELAY_MS = 30
const TAB_HOVER_CARD_CLOSE_DELAY_MS = 40
const NEW_TAB_TICKER_WATCHDOG_MS = 1_000

// The hover card renders in one of two sizes (see Figma: md adds a third "Funding/1h"
// stat and roomier padding; sm drops it for a compact fixed-height row).
export type HoverCardSize = 'sm' | 'md'

/** An instrument a tab strip shows: what the hover card and its live-ticker feed key by. */
export interface TabInstrument {
  exchange?: Exchange
  symbol: string
  /** Options stream on the incremental-ticker channel — the plain ticker carries no 24h
   *  change for them — and have no funding rate. */
  isOption?: boolean
}

function formatPreviewPrice(value: string | undefined): string {
  if (!value) return '—'
  const numberValue = Number(value)
  if (!Number.isFinite(numberValue)) return '—'

  const maximumFractionDigits =
    Math.abs(numberValue) >= 1_000 ? 2 : Math.abs(numberValue) >= 1 ? 4 : 6
  return numberValue.toLocaleString('en-US', { maximumFractionDigits })
}

/** `ticker.fundingRate` is a fraction (e.g. "0.000001"), matching the `* 100` convention
 *  already used for display in quick-order-contract-details.tsx. */
function formatFundingRatePercent(value: string | undefined): {
  text: string
  percent: number | null
} {
  if (!value) return { text: '—', percent: null }
  const numberValue = Number(value)
  if (!Number.isFinite(numberValue)) return { text: '—', percent: null }

  const percent = numberValue * 100
  return { text: `${percent >= 0 ? '+' : ''}${percent.toFixed(4)}%`, percent }
}

/** Streams tickers for a tab strip's instruments into the shared ticker map the hover card reads. */
export function useTabMarketData(instruments: TabInstrument[]) {
  const store = useStore()
  const { payloads, optionPayloads } = useMemo(() => {
    const seen = new Set<string>()
    const tickers: { exchange: Exchange; symbol: string; dataType: MarketDataType }[] = []
    const options: typeof tickers = []

    for (const { exchange, symbol, isOption } of instruments) {
      if (exchange === undefined) continue

      const key = watchlistEntryKey(exchange, symbol)
      if (!symbol || seen.has(key)) continue

      seen.add(key)
      if (isOption) {
        options.push({
          exchange,
          symbol,
          dataType: MarketDataType.MARKET_DATA_TYPE_INCREMENTAL_TICKER,
        })
      } else {
        tickers.push({ exchange, symbol, dataType: MarketDataType.MARKET_DATA_TYPE_TICKER })
      }
    }
    return { payloads: tickers, optionPayloads: options }
  }, [instruments])

  const handleMessage = useCallback(
    (data: TickerList) => {
      if (!data.tickers.length) return

      for (const ticker of data.tickers) {
        const key = watchlistEntryKey(ticker.exchange, ticker.symbol)
        scheduleTickerMapWrite(store, key, (current) => {
          // A field genuinely not applicable for this symbol (e.g. markPrice on a
          // Bybit inverse-perp) arrives as proto3's zero-value '' rather than being
          // omitted, so a plain `??` fallback would stamp '' over a previously good
          // cached value. Fall back to `current` for '' too, matching the sticky-merge
          // guard chart-market-stats-row.tsx uses against this same shared cache.
          const pick = (incoming: string | undefined, existing: string | undefined) =>
            incoming ? incoming : existing
          const lastPrice = pick(ticker.lastPrice, current?.lastPrice)
          const markPrice = pick(ticker.markPrice, current?.markPrice)
          const indexPrice = pick(ticker.indexPrice, current?.indexPrice)
          const priceChange24h = pick(ticker.priceChange24h, current?.priceChange24h)
          const priceChangePercent24h = pick(
            ticker.priceChangePercent24h,
            current?.priceChangePercent24h
          )
          const fundingRate = pick(ticker.fundingRate, current?.fundingRate)
          const openInterest = pick(ticker.openInterest, current?.openInterest)

          if (
            current?.lastPrice === lastPrice &&
            current?.markPrice === markPrice &&
            current?.indexPrice === indexPrice &&
            current?.priceChange24h === priceChange24h &&
            current?.priceChangePercent24h === priceChangePercent24h &&
            current?.fundingRate === fundingRate &&
            current?.openInterest === openInterest
          ) {
            return current
          }

          return {
            ...current,
            lastPrice,
            markPrice,
            indexPrice,
            prevLastPrice: current?.lastPrice ?? lastPrice,
            priceChange24h,
            priceChangePercent24h,
            fundingRate,
            openInterest,
          }
        })
      }
    },
    [store]
  )

  const { flushAndReconnect } = useMultiMarketDataStream({
    connected: payloads.length > 0,
    payloads,
    onMessage: handleMessage,
  })

  // An option's 24h change only arrives as `stats.priceChangePercent` on its incremental
  // ticker (same as the watchlist's option rows), so it's read from there.
  const handleOptionMessage = useCallback(
    (data: IncrementalTickerList | undefined) => {
      if (!data?.incrementalTickers?.length) return

      for (const ticker of data.incrementalTickers) {
        const key = watchlistEntryKey(ticker.exchange, ticker.symbol)
        scheduleTickerMapWrite(store, key, (current) => {
          const pick = (incoming: string | undefined, existing: string | undefined) =>
            incoming ? incoming : existing
          const lastPrice = pick(ticker.lastPrice, current?.lastPrice)
          const markPrice = pick(ticker.markPrice, current?.markPrice)
          const indexPrice = pick(ticker.indexPrice, current?.indexPrice)
          const priceChangePercent24h = pick(
            ticker.stats?.priceChangePercent,
            current?.priceChangePercent24h
          )
          const openInterest = pick(ticker.openInterest, current?.openInterest)

          if (
            current?.lastPrice === lastPrice &&
            current?.markPrice === markPrice &&
            current?.indexPrice === indexPrice &&
            current?.priceChangePercent24h === priceChangePercent24h &&
            current?.openInterest === openInterest
          ) {
            return current
          }

          return {
            ...current,
            lastPrice,
            markPrice,
            indexPrice,
            prevLastPrice: current?.lastPrice ?? lastPrice,
            priceChangePercent24h,
            openInterest,
          }
        })
      }
    },
    [store]
  )

  useMultiIncrementalInstrumentStream({
    connected: optionPayloads.length > 0,
    payloads: optionPayloads,
    onMessage: handleOptionMessage,
  })

  const knownPayloadKeysRef = useRef<Set<string> | null>(null)
  const activePayloadKeysRef = useRef(new Set<string>())
  const pendingRefreshesRef = useRef(new Map<string, (typeof payloads)[number]>())
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const currentKeys = new Set(
      payloads.map((payload) => watchlistEntryKey(payload.exchange, payload.symbol))
    )
    activePayloadKeysRef.current = currentKeys

    for (const key of pendingRefreshesRef.current.keys()) {
      if (!currentKeys.has(key)) pendingRefreshesRef.current.delete(key)
    }

    const previousKeys = knownPayloadKeysRef.current
    knownPayloadKeysRef.current = currentKeys
    if (previousKeys !== null) {
      const tickerMap = store.get(watchListTickerMapAtom)
      for (const payload of payloads) {
        const key = watchlistEntryKey(payload.exchange, payload.symbol)
        if (!previousKeys.has(key) && !tickerMap[key]) {
          pendingRefreshesRef.current.set(key, payload)
        }
      }

      if (pendingRefreshesRef.current.size > 0 && !refreshTimeoutRef.current) {
        refreshTimeoutRef.current = setTimeout(() => {
          refreshTimeoutRef.current = null
          const latestTickerMap = store.get(watchListTickerMapAtom)
          const missingPayloads = Array.from(pendingRefreshesRef.current.entries()).flatMap(
            ([key, payload]) =>
              activePayloadKeysRef.current.has(key) && !latestTickerMap[key] ? [payload] : []
          )
          pendingRefreshesRef.current.clear()

          if (missingPayloads.length > 0) flushAndReconnect(missingPayloads)
        }, NEW_TAB_TICKER_WATCHDOG_MS)
      }
    }

    return () => {
      if (!refreshTimeoutRef.current) return
      clearTimeout(refreshTimeoutRef.current)
      refreshTimeoutRef.current = null
    }
  }, [flushAndReconnect, payloads, store])

  useEffect(() => () => pendingRefreshesRef.current.clear(), [])
}

/** One label/value cell in the stat row — bordered on the left except when it's the
 *  first cell in the row (the divider between cells, not the row's own edge). */
function TabMarketStatCell({
  label,
  value,
  valueClassName,
  bordered,
}: {
  label: string
  value: string
  valueClassName?: string
  bordered: boolean
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 flex-col gap-1 px-2',
        bordered && 'border-foreground/10 border-l'
      )}
    >
      <div className="text-foreground/40 truncate text-[10px] leading-tight tracking-tight">
        {label}
      </div>
      <div
        className={cn(
          'truncate text-[10px] leading-tight font-medium tabular-nums',
          valueClassName
        )}
      >
        {value}
      </div>
    </div>
  )
}

export function TabMarketPreview({
  exchange: tabExchange,
  symbol,
  isOption = false,
  cardSize,
}: TabInstrument & { cardSize: HoverCardSize }) {
  const exchange = tabExchange ?? Exchange.EXCHANGE_UNSPECIFIED
  const ticker = useWatchlistLiveTicker(watchlistEntryKey(exchange, symbol))
  const price = displayPriceOf(ticker)
  const rawChange = resolvePriceChangePercent24h(exchange, ticker)
  const change = rawChange === undefined ? null : Number(rawChange)
  const hasChange = change !== null && Number.isFinite(change)
  const funding = formatFundingRatePercent(ticker.fundingRate)
  const isMd = cardSize === 'md'

  const changeClassName = hasChange
    ? change >= 0
      ? 'text-chart-mint'
      : 'text-chart-red'
    : 'text-foreground/40'
  const fundingClassName =
    funding.percent !== null
      ? funding.percent >= 0
        ? 'text-chart-mint'
        : 'text-chart-red'
      : 'text-foreground/40'

  return (
    <div className={isMd ? 'w-[220px]' : 'w-[195px]'}>
      <div className="flex min-w-0 items-center gap-2.5 px-2 pt-2 pb-1.5">
        {/* An unknown venue draws no logo — toExchangeString would fall back to Deribit's. */}
        {exchange !== Exchange.EXCHANGE_UNSPECIFIED && (
          <ExchangeIcon
            className="size-8 shrink-0 rounded-full"
            exchange={getExchangeIconKey(exchange) ?? toExchangeString(exchange)}
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm leading-tight font-medium tracking-tight">
            {formatSymbol(symbol)}
          </div>
          <div className="text-foreground/60 mt-0.5 truncate text-[10px] leading-tight">
            {getExchangeLabel(exchange)}
          </div>
        </div>
      </div>

      <div
        className={cn(
          'border-foreground/10 flex items-center border-t',
          isMd ? 'py-3' : 'h-[46px]'
        )}
      >
        <TabMarketStatCell
          label={formatFirstLetterToUpperCase('mark')}
          value={formatPreviewPrice(price)}
          bordered={false}
        />
        <TabMarketStatCell
          label={formatFirstLetterToUpperCase('24h')}
          value={hasChange ? `${change >= 0 ? '+' : ''}${change.toFixed(2)}%` : '—'}
          valueClassName={changeClassName}
          bordered
        />
        {isMd && !isOption && (
          <TabMarketStatCell
            label="Funding/1h"
            value={funding.text}
            valueClassName={fundingClassName}
            bordered
          />
        )}
      </div>
    </div>
  )
}

/**
 * One shared hover-card per tab strip instead of one Radix HoverCard.Root per tab (each wiring
 * up its own pointer listeners/context even while closed). Hovering a tab just relocates this
 * single popover's virtual anchor to that tab's own DOM element and swaps which tab's key it
 * shows, instead of mounting/tearing down a whole separate hover-card tree per tab.
 *
 * `disabled` (e.g. while a tab is being dragged) closes it and keeps it closed.
 */
export function useTabHoverCard({ disabled = false }: { disabled?: boolean } = {}) {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null)
  // Radix's virtualRef wants a ref whose `.current` is always Measurable (never null) —
  // parked at the origin until a tab is actually hovered, harmless since the anchor is
  // only ever read while the popover is open (i.e. after a real element has been set).
  const anchorRef = useRef<{ getBoundingClientRect: () => DOMRect }>({
    getBoundingClientRect: () => new DOMRect(),
  })
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimeouts = useCallback(() => {
    if (openTimeoutRef.current) clearTimeout(openTimeoutRef.current)
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    openTimeoutRef.current = null
    closeTimeoutRef.current = null
  }, [])

  /** Closes immediately, no delay — pointer-down (click or drag start), a removed tab… */
  const close = useCallback(() => {
    clearTimeouts()
    setHoveredKey(null)
  }, [clearTimeouts])

  /** Hover/focus entered a tab — relocate the card there (after the open delay, or
   *  immediately if one is already showing). */
  const hoverStart = useCallback(
    (key: string, element: HTMLElement) => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
        closeTimeoutRef.current = null
      }
      const relocate = () => {
        anchorRef.current = element
        setHoveredKey(key)
      }
      // Already showing another tab's preview — relocate immediately instead of waiting
      // out the open delay again, so sweeping across adjacent tabs feels like one
      // continuously-repositioned surface rather than N independently-timed popups.
      if (hoveredKey !== null) {
        if (openTimeoutRef.current) {
          clearTimeout(openTimeoutRef.current)
          openTimeoutRef.current = null
        }
        relocate()
        return
      }
      if (openTimeoutRef.current) clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = setTimeout(relocate, TAB_HOVER_CARD_OPEN_DELAY_MS)
    },
    [hoveredKey]
  )

  /** Hover/focus left a tab — close after the close delay, unless the pointer lands on the
   *  card's own content first. */
  const hoverEnd = useCallback(() => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredKey(null)
    }, TAB_HOVER_CARD_CLOSE_DELAY_MS)
  }, [])

  // Moving from the trigger tab straight into the card's own content must not close it —
  // mirrors Radix HoverCard's default hoverable-content behavior, which drove this before.
  const contentMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    if (disabled) close()
  }, [disabled, close])

  useEffect(() => clearTimeouts, [clearTimeouts])

  return {
    hoveredKey: disabled ? null : hoveredKey,
    anchorRef,
    hoverStart,
    hoverEnd,
    close,
    contentMouseEnter,
  }
}

/**
 * The popover half of useTabHoverCard. `children` is only rendered while open; pass null when
 * the hovered tab no longer exists so the content goes in the same commit as its anchor, before
 * Radix can try to reposition against a detached element.
 */
export function TabHoverCard({
  card,
  side = 'bottom',
  children,
}: {
  card: ReturnType<typeof useTabHoverCard>
  side?: 'top' | 'bottom'
  children: ReactNode
}) {
  const isOpen = children !== null && children !== undefined && children !== false

  // Smoothly animates the card sliding to the next tab when it's already open, but skips
  // the animation for a fresh open. Popper positions the card via an inline `transform` on
  // its own wrapper div (not exposed as an element we can ref directly — see the
  // [&_[data-radix-popper-content-wrapper]] selector below), resolved in a layout effect
  // before first paint; a transition targeting that same property would otherwise also
  // animate THAT one-time jump from its pre-measurement placeholder position, which reads
  // as the card flying in from the wrong place instead of appearing where it should.
  // Enabling the transition one frame after open — once the first position has already
  // landed and painted — means it's only ever live for a genuine same-session relocation,
  // not the initial placement.
  const [allowRepositionTransition, setAllowRepositionTransition] = useState(false)
  useEffect(() => {
    if (!isOpen) {
      setAllowRepositionTransition(false)
      return
    }
    const id = requestAnimationFrame(() => setAllowRepositionTransition(true))
    return () => cancelAnimationFrame(id)
  }, [isOpen])

  return (
    <PopoverPrimitive.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) card.close()
      }}
    >
      <PopoverPrimitive.Anchor virtualRef={card.anchorRef} />
      {isOpen && (
        <PopoverPrimitive.Portal>
          {/* Popper's own position wrapper (`[data-radix-popper-content-wrapper]`) isn't
              exposed as a styleable element — this plain, unstyled-by-default div is
              purely a scoped selector anchor for animating ITS inline transform once
              the card is already open (see allowRepositionTransition above). */}
          <div
            className={cn(
              allowRepositionTransition &&
                '[&_[data-radix-popper-content-wrapper]]:transition-[transform_150ms_ease-out] [&_[data-radix-popper-content-wrapper]]:motion-reduce:transition-none'
            )}
          >
            <PopoverPrimitive.Content
              side={side}
              align="start"
              sideOffset={6}
              onOpenAutoFocus={(event) => event.preventDefault()}
              onMouseEnter={card.contentMouseEnter}
              onMouseLeave={card.hoverEnd}
              className="menu-surface data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-auto origin-[--radix-popover-content-transform-origin] overflow-hidden duration-100 outline-none"
            >
              {children}
            </PopoverPrimitive.Content>
          </div>
        </PopoverPrimitive.Portal>
      )}
    </PopoverPrimitive.Root>
  )
}
